import { Trans } from '@lingui/react/macro';
import { FabricEditor, FabricEditorCanvas, FabricEditorProvider } from 'modules/fabric-editor';
import { MediaManagerTemplate } from 'modules/core/domain/interfaces/supabase/media-manager/entity';
import React, { useEffect, useRef, useState } from 'react';
import { ensureFonts } from 'shared/fonts';
import { RENDER_PLUGINS, loadCanvasJson, nextFrame } from './headless-canvas';
import { PRESET_PREVIEW_FRAME_CLASS } from './preset-card-metrics';

interface Props {
  template: MediaManagerTemplate;
  /** Longest-side target size of the generated thumbnail, in px. */
  targetPx?: number;
}

/**
 * Renders a template's authored canvas off-screen (same headless technique as the
 * render pages) and shows a downscaled PNG thumbnail. No placeholder substitution —
 * the preview reflects the template as authored (placeholder cues included).
 */
const MediaManagerPresetPreview: React.FC<Props> = ({ template, targetPx = 480 }) => {
  const [editor, setEditor] = useState<FabricEditor | null>(null);
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const captured = useRef(false);
  /**
   * Leaving the page while thumbnails are still being built unmounts the card mid-load: no
   * `toDataURL` on a disposed canvas and no `setState` after that (IMGM-1521). A mount flag, not the
   * capture effect's cleanup — under StrictMode that cleanup runs right away, and `captured` would
   * then never let the capture start again.
   */
  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const isEmpty = !template.objects || template.objects.length === 0;

  useEffect(() => {
    if (isEmpty || !editor || captured.current) return;
    captured.current = true;

    const isGone = () => !isMounted.current || editor.isDisposed();

    (async () => {
      try {
        // Before the load, as in the editor: the placeholder cue turns red by the
        // type's rules, and without a type a player key on «Статистика игроков» or
        // «День рождения» asks for «Команда №» it never has.
        editor.placeholders?.setTemplateType(template.templateType);
        await loadCanvasJson(editor, template.objects ?? []);
        // Шрифты грузятся лениво (CSS2 Google): канва их сама не просит, а снимок делается один раз —
        // без явного ожидания миниатюра навсегда осталась бы с запасным шрифтом.
        await ensureFonts(template.objects ?? []);
        editor.fonts?.refresh();
        await nextFrame();
        if (isGone()) return;
        const multiplier = Math.min(1, targetPx / Math.max(template.width, template.height));
        setDataUrl(editor.canvas.toDataURL({ format: 'png', multiplier }));
      } catch (error) {
        if (isGone()) return;
        console.error('Failed to render preset preview:', error);
        setFailed(true);
      }
    })();
  }, [editor, isEmpty, targetPx, template]);

  return (
    <div className={PRESET_PREVIEW_FRAME_CLASS}>
      {isEmpty ? (
        <span className="text-sm text-gray-400">
          <Trans>Пустой шаблон</Trans>
        </span>
      ) : failed ? (
        <span className="text-sm text-gray-400">
          <Trans>Не удалось построить превью</Trans>
        </span>
      ) : dataUrl ? (
        <img src={dataUrl} alt={template.name} className="h-full w-full object-contain" />
      ) : (
        // Миниатюра строится офскрин-канвой — пока её нет, квадрат занят плашкой.
        <div className="skeleton-block absolute inset-0" data-testid="preset-preview-skeleton" aria-hidden />
      )}

      {/* Off-screen editor: mounted only until the thumbnail is captured. */}
      {!isEmpty && !dataUrl && !failed && (
        <div style={{ position: 'fixed', left: -99999, top: -99999, pointerEvents: 'none' }} aria-hidden>
          <FabricEditorProvider editor={editor}>
            <FabricEditorCanvas
              key={`${template.id}-${template.width}x${template.height}`}
              width={template.width}
              height={template.height}
              resolution={{ width: template.width, height: template.height }}
              plugins={RENDER_PLUGINS}
              onReady={setEditor}
            />
          </FabricEditorProvider>
        </div>
      )}
    </div>
  );
};

export default React.memo(MediaManagerPresetPreview);
